/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { Trash2 } from 'lucide-react';
import { useFieldArray, useFormContext, UseFormReturn, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import Input from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { FormControl, FormField, FormItem } from '../../../components/ui/form';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { formatNumber, parseNumber } from '../../../utils/format';
import { FormSchema } from './Schema';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import 'react-quill/dist/quill.bubble.css';

interface ProductListFormProps {
  form: UseFormReturn<z.infer<typeof FormSchema>>;
}

interface ProductRowProps {
  index: number;
  onRemove: (index: number) => void;
  form: UseFormReturn<z.infer<typeof FormSchema>>;
}

function ProductRow({ index, onRemove, form }: ProductRowProps) {
  const { control, setValue } = useFormContext();

  const quantity = useWatch({
    control,
    name: `invoice_products.${index}.quantity`,
    defaultValue: 0,
  });

  const price = useWatch({
    control,
    name: `invoice_products.${index}.price`,
    defaultValue: 0,
  });

  const discount = useWatch({
    control,
    name: `invoice_products.${index}.discount`,
    defaultValue: 0,
  });

  const tax = useWatch({
    control,
    name: `invoice_products.${index}.tax`,
    defaultValue: 0,
  });

  const discountType = useWatch({
    control,
    name: `invoice_products.${index}.discount_type`,
    defaultValue: 'percentage',
  });

  useEffect(() => {
    let discountAmount = 0;
    if (discountType === 'percentage') {
      discountAmount = (discount / 100) * price;
    } else if (discountType === 'rupiah') {
      discountAmount = discount;
    }
    const taxAmount = (tax / 100) * (price - discountAmount);
    const total = quantity * price - discountAmount + taxAmount;

    setValue(`invoice_products.${index}.amount`, total);
  }, [quantity, price, discount, tax, discountType, setValue, index]);

  return (
    <TableRow>
      <TableCell className="font-medium px-1">
        <FormField
          name={`invoice_products.${index}.name`}
          control={control}
          render={({ field }) => (
            <FormItem>
              <Input
                {...field}
                placeholder="Product Name"
                className={`${form.formState.errors.invoice_products?.[index]?.name ? 'border-red-500' : ''}w-36`}
                required
              />
            </FormItem>
          )}
        />
      </TableCell>
      <TableCell className="px-1">
        <FormField
          name={`invoice_products.${index}.description`}
          control={control}
          render={({ field }) => (
            <FormItem>
              <ReactQuill
                theme="bubble"
                placeholder="Product Description"
                className={`${
                  form.formState.errors.invoice_products?.[index]?.description ? 'border-red-500' : ''
                } min-w-40 rounded-md border border-neutral-200`}
                {...field}
              />
            </FormItem>
          )}
        />
      </TableCell>
      <TableCell className="px-1 w-10">
        <FormField
          name={`invoice_products.${index}.quantity`}
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              className={`${
                form.formState.errors.invoice_products?.[index]?.quantity ? 'border-red-500' : ''
              } w-10 p-0 text-center`}
              value={formatNumber(field.value)}
              onChange={(e) => {
                field.onChange(parseNumber(e.target.value));
              }}
              required
            />
          )}
        />
      </TableCell>
      <TableCell className="text-right px-1 w-10">
        <FormField
          name={`invoice_products.${index}.price`}
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              className={`${
                form.formState.errors.invoice_products?.[index]?.price ? 'border-red-500' : ''
              } text-right w-32`}
              value={formatNumber(field.value)}
              onChange={(e) => field.onChange(parseNumber(e.target.value))}
            />
          )}
        />
      </TableCell>
      <TableCell className="text-right px-1">
        <div className="flex flex-row gap-1">
          <FormField
            name={`invoice_products.${index}.discount_type`}
            control={control}
            render={({ field }) => (
              <FormItem>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="%" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="percentage">%</SelectItem>
                    <SelectItem value="rupiah">Rp</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
          <FormField
            name={`invoice_products.${index}.discount`}
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                className={`${
                  form.formState.errors.invoice_products?.[index]?.discount ? 'border-red-500' : ''
                } w-24 text-right`}
                value={formatNumber(field.value)}
                onChange={(e) => field.onChange(parseNumber(e.target.value))}
              />
            )}
          />
        </div>
      </TableCell>
      <TableCell className="text-right px-1 w-10">
        <FormField
          name={`invoice_products.${index}.tax`}
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              className={`${
                form.formState.errors.invoice_products?.[index]?.tax ? 'border-red-500' : ''
              } w-10 p-0 text-center`}
              value={formatNumber(field.value)}
              onChange={(e) => field.onChange(parseNumber(e.target.value))}
            />
          )}
        />
      </TableCell>
      <TableCell className="text-right px-1 w-32">
        <FormField
          name={`invoice_products.${index}.amount`}
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              placeholder="Amount"
              className={`${
                form.formState.errors.invoice_products?.[index]?.amount ? 'border-red-500' : ''
              } text-right ml-auto w-32`}
              value={formatNumber(field.value)}
              disabled
            />
          )}
        />
      </TableCell>
      <TableCell className="text-right px-1 w-16">
        <Button type="button" variant="destructive" onClick={() => onRemove(index)}>
          <Trash2 size={16} />
        </Button>
      </TableCell>
    </TableRow>
  );
}

export default function ProductListForm({ form }: ProductListFormProps) {
  const [financialData, setFinancialData] = useState([
    { detail: 'Subtotal', amount: 0 },
    { detail: 'Total Discount', amount: 0 },
    { detail: 'Tax', amount: 0 },
    { detail: 'Total', amount: 0 },
  ]);
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'invoice_products',
  });

  const invoiceProducts = useWatch({
    control: form.control,
    name: 'invoice_products',
  });

  useEffect(() => {
    let subtotal = 0;
    let totalDiscount = 0;
    let tax = 0;

    invoiceProducts?.forEach((product) => {
      const productTotal = product.quantity * product.price;
      subtotal += productTotal;
      totalDiscount +=
        product.discount_type === 'percentage' ? (product.discount / 100) * productTotal : product.discount;
      tax += (product.tax / 100) * (productTotal - totalDiscount);
    });

    const total = subtotal - totalDiscount + tax;

    // Update the form fields with calculated values
    form.setValue('subtotal', subtotal);
    form.setValue('tax', tax);
    form.setValue('total', total);
    form.setValue('amount_due', total); // Setting amount_due equal to total

    setFinancialData([
      { detail: 'Subtotal', amount: subtotal },
      { detail: 'Total Discount', amount: totalDiscount },
      { detail: 'Tax', amount: tax },
      { detail: 'Total', amount: total },
    ]);
  }, [invoiceProducts, form]); // Added form to dependencies

  return (
    <>
      <div className="flex flex-col gap-4 mt-8">
        <p className="text-base font-medium">Product List</p>
        <div className="w-[calc(100dvw-32px)] lg:w-full">
          <Table className=" overflow-x-scroll">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px] px-1">
                  Product <span className="text-red-600">*</span>
                </TableHead>
                <TableHead className="px-1">
                  Description <span className="text-red-600">*</span>
                </TableHead>
                <TableHead className="px-1 w-max">
                  Qty <span className="text-red-600">*</span>
                </TableHead>
                <TableHead className="text-right px-1">
                  Price <span className="text-red-600">*</span>
                </TableHead>
                <TableHead className="text-right w-10 px-1">Discount </TableHead>
                <TableHead className="text-right px-1 w-max">Tax(%)</TableHead>
                <TableHead className="text-right px-1 w-max">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fields.map((field, index) => (
                <ProductRow key={field.id} index={index} onRemove={remove} form={form} />
              ))}
            </TableBody>
          </Table>
        </div>
        <Button
          variant="orangeOutline"
          className="w-max"
          type="button"
          onClick={() =>
            append({
              name: '',
              description: '',
              quantity: 0,
              price: 0,
              discount: 0,
              tax: 0,
              amount: 0,
              discount_type: 'percentage',
            })
          }
        >
          Add line
        </Button>
      </div>
      <div className="md:w-1/2 w-full my-8">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Detail</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {financialData.map((item) => (
              <TableRow key={item.detail}>
                <TableCell>{item.detail}</TableCell>
                <TableCell className="text-right">
                  Rp {item.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Hidden form fields to store financial data */}
      <div className="hidden">
        <FormField control={form.control} name="subtotal" render={({ field }) => <Input {...field} type="hidden" />} />
        <FormField control={form.control} name="tax" render={({ field }) => <Input {...field} type="hidden" />} />
        <FormField control={form.control} name="total" render={({ field }) => <Input {...field} type="hidden" />} />
        <FormField
          control={form.control}
          name="amount_due"
          render={({ field }) => <Input {...field} type="hidden" />}
        />
      </div>
    </>
  );
}
